import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { cx } from '@/ui/utils/cx';

export type AppMenuItem = {
  id: string;
  label: string;
  icon?: React.ReactNode;
  onSelect?: () => void;
  href?: string;
  disabled?: boolean;
  destructive?: boolean;
};

export type AppMenuProps = {
  label: string;
  items: AppMenuItem[];
  note?: string;
  align?: 'start' | 'end';
  triggerClassName?: string;
  minWidth?: number;
};

export default function AppMenu({
  label,
  items,
  note,
  align = 'end',
  triggerClassName,
  minWidth = 220,
}: AppMenuProps) {
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const selectableItems = useMemo(
    () => items.filter((item) => !item.disabled && (item.onSelect || item.href)),
    [items],
  );

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const width = Math.max(minWidth, rect.width);
    const left =
      align === 'end'
        ? Math.min(Math.max(8, rect.right - width), window.innerWidth - width - 8)
        : Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
    setPosition({ top: rect.bottom + 6, left });
  }, [align, minWidth]);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    function onScroll() {
      updatePosition();
    }
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (open) setHighlightIndex(0);
  }, [open, items]);

  const close = () => setOpen(false);

  const activateItem = (item: AppMenuItem) => {
    if (item.disabled || (!item.onSelect && !item.href)) return;
    close();
    item.onSelect?.();
  };

  const onPanelKeyDown = (event: React.KeyboardEvent) => {
    if (!selectableItems.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightIndex((index) => (index + 1) % selectableItems.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightIndex((index) => (index - 1 + selectableItems.length) % selectableItems.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const item = selectableItems[highlightIndex];
      if (item) activateItem(item);
    }
  };

  const highlightedId = selectableItems[highlightIndex]?.id;

  return (
    <div className="app-menu">
      <button
        ref={triggerRef}
        type="button"
        className={cx('app-menu__trigger btn btn-sm btn-ghost', triggerClassName)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={menuId}
        aria-label={label}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="app-menu__trigger-icon" aria-hidden>
          ⋮
        </span>
        <span className="app-menu__trigger-label">Actions</span>
      </button>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            id={menuId}
            className="app-menu__panel"
            role="menu"
            aria-label={label}
            style={{ top: position.top, left: position.left, minWidth }}
            onKeyDown={onPanelKeyDown}
          >
            {items.map((item) => {
              const highlighted = highlightedId === item.id;
              const itemClass = cx(
                'app-menu__item',
                item.destructive && 'app-menu__item--danger',
                item.disabled && 'app-menu__item--disabled',
                highlighted && 'is-highlighted',
              );
              const content = (
                <>
                  {item.icon ? <span className="app-menu__item-icon">{item.icon}</span> : null}
                  <span className="app-menu__item-label">{item.label}</span>
                </>
              );
              if (item.href && !item.disabled) {
                return (
                  <Link
                    key={item.id}
                    to={item.href}
                    role="menuitem"
                    className={itemClass}
                    onClick={close}
                  >
                    {content}
                  </Link>
                );
              }
              return (
                <button
                  key={item.id}
                  type="button"
                  role="menuitem"
                  className={itemClass}
                  disabled={item.disabled}
                  onClick={() => activateItem(item)}
                >
                  {content}
                </button>
              );
            })}
            {note ? <p className="app-menu__note">{note}</p> : null}
          </div>,
          document.body,
        )}
    </div>
  );
}
